import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { resetPasswordApi } from "@/api";

export default function ResetPasswordPage() {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const isRTL = i18n.language === "ar";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      setError(t("resetTokenMissing"));
      return;
    }

    if (password.length < 6) {
      setError(t("passwordMinError"));
      return;
    }

    if (password !== confirmPassword) {
      setError(t("passwordsDoNotMatch"));
      return;
    }

    setError("");
    setLoading(true);

    try {
      await resetPasswordApi({ token, password });
      setSuccess(true);
    } catch (err: any) {
      console.error(err);
      setError(t("resetPasswordFailed"));
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 outline-none text-sm transition-all text-start";

  return (
    <div className="min-h-screen bg-[#FFFBF7] pt-24 md:pt-32 pb-8 flex items-center justify-center">
      <div className="w-full px-4 max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-primary-400/10 flex items-center justify-center mx-auto mb-4">
            <i className="ri-key-2-line text-primary-400 text-2xl" />
          </div>
          <h1 className="text-xl md:text-2xl font-bold text-navy-400 mb-2">
            {t("resetPasswordTitle")}
          </h1>
          <p className="text-sm text-gray-500 leading-relaxed">
            {t("resetPasswordDesc")}
          </p>
        </div>

        {success ? (
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 shadow-sm text-center space-y-5">
            <div className="w-16 h-16 rounded-full bg-green-100 text-green-500 flex items-center justify-center mx-auto">
              <i className="ri-checkbox-circle-line text-3xl" />
            </div>
            <h3 className="text-lg font-bold text-gray-800">
              {t("passwordChangedSuccess")}
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed">
              {t("passwordChangedDesc")}
            </p>
            <button
              onClick={() => navigate("/")}
              className="w-full py-3.5 bg-primary-400 hover:bg-primary-500 text-white rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2"
            >
              <i className="ri-home-4-line" />
              <span>{t("goHomeToLogin")}</span>
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 shadow-sm space-y-5"
          >
            {error && (
              <div className="p-3 bg-red-50 text-red-500 rounded-xl text-xs flex items-center gap-2">
                <i className="ri-error-warning-line text-sm" />
                <span>{error}</span>
              </div>
            )}

            {!token && (
              <div className="p-3 bg-yellow-50 text-yellow-600 rounded-xl text-xs flex items-center gap-2">
                <i className="ri-alert-line text-sm" />
                <span>{t("resetTokenMissingWarning")}</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 text-start">
                {t("newPassword")}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={inputClass}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1"
                  style={{ [isRTL ? "left" : "right"]: "12px" }}
                  tabIndex={-1}
                  aria-label={showPassword ? t("hidePassword") : t("showPassword")}
                  title={showPassword ? t("hidePassword") : t("showPassword")}
                >
                  <i className={showPassword ? "ri-eye-off-line text-lg" : "ri-eye-line text-lg"} />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 text-start">
                {t("confirmNewPassword")}
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className={inputClass}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1"
                  style={{ [isRTL ? "left" : "right"]: "12px" }}
                  tabIndex={-1}
                  aria-label={showConfirmPassword ? t("hidePassword") : t("showPassword")}
                  title={showConfirmPassword ? t("hidePassword") : t("showPassword")}
                >
                  <i className={showConfirmPassword ? "ri-eye-off-line text-lg" : "ri-eye-line text-lg"} />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !token}
              className="w-full py-3.5 bg-primary-400 hover:bg-primary-500 disabled:bg-gray-300 text-white rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
            >
              {loading ? (
                <span>{t("saving")}</span>
              ) : (
                <>
                  <i className="ri-save-3-line" />
                  <span>{t("savePassword")}</span>
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
